import { useLocation } from "react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useCurrentUser from "../../hooks/useCurrentUser";
import { usePropertiesOverviewPrefetch } from "../../hooks/properties";
import { useAssetsOverviewPrefetch } from "../../hooks/assets";
import { useTagsPrefetch } from "../../hooks/useTags";
import usePageBreakpoints from "../../hooks/usePageBreakpoints";
import { useAppState } from "../../state/appState";
import {
  NAVBAR_SLIDE_OUTS,
  SET_MAIN_REF,
  SET_SCREEN_SIZE,
} from "../../constants";
import useDocumentsConfiguration from "../../hooks/useDocumentsConfiguration";
import useManagementConfiguration from "../../hooks/useManagementConfiguration";

const useMainContainer = ({ isRegistered, isLoading }) => {
  const location = useLocation();
  const { data: currentUser } = useCurrentUser();
  const [, dispatch] = useAppState();

  // Prefetch Overviews
  const { prefetchPropertiesOverview } = usePropertiesOverviewPrefetch();
  const { prefetchAssetsOverview } = useAssetsOverviewPrefetch();

  prefetchPropertiesOverview();
  prefetchAssetsOverview();
  useTagsPrefetch();
  useDocumentsConfiguration();

  const { data: managementConfiguration } = useManagementConfiguration();

  const { breakpoint } = usePageBreakpoints();

  const mainContainerScroll = useRef();

  const [slideOut, setSlideOut] = useState(NAVBAR_SLIDE_OUTS);

  useEffect(() => {
    dispatch({
      type: SET_SCREEN_SIZE,
      screenSize: breakpoint,
    });
  }, [dispatch, breakpoint]);

  const handleMainContainerRef = useCallback(
    (el) => {
      mainContainerScroll.current = el;
      dispatch({
        type: SET_MAIN_REF,
        ref: mainContainerScroll.current,
      });
    },
    [dispatch]
  );

  const lilyEnabled = useMemo(() => {
    return managementConfiguration?.management?.lily?.isEnabled;
  }, [managementConfiguration?.management?.lily?.isEnabled]);

  const showMainContent = useMemo(() => {
    return !isLoading && isRegistered;
  }, [isLoading, isRegistered]);

  const showLoadingSpinner = useMemo(() => {
    return isLoading || !currentUser?.id;
  }, [currentUser?.id, isLoading]);

  const toggleSlideOut = (value) => setSlideOut(value);

  const hideNotifications =
    !isRegistered && currentUser?.permissions?.ticket?.can_create;

  return {
    slideOut,
    location,
    lilyEnabled,
    toggleSlideOut,
    showMainContent,
    hideNotifications,
    showLoadingSpinner,
    handleMainContainerRef,
  };
};

export default useMainContainer;
